<template>
  <div class="audit-log-view">
    <!-- 头部搜索和过滤区域 -->
    <div class="audit-header">
      <div class="search-filters">
        <div class="search-group">
          <input
            v-model="searchQuery"
            type="text"
            :placeholder="t('audit.searchPlaceholder')"
            class="search-input"
            @input="debounceSearch"
          />
          <i class="fas fa-search search-icon"></i>
        </div>

        <div class="filter-group">
          <select v-model="selectedAction" class="filter-select">
            <option value="">{{ t('audit.allActions') }}</option>
            <option v-for="action in availableActions" :key="action" :value="action">
              {{ getActionDisplayName(action) }}
            </option>
          </select>

          <select v-model="selectedStatus" class="filter-select">
            <option value="">{{ t('audit.allStatus') }}</option>
            <option value="success">{{ t('audit.success') }}</option>
            <option value="failed">{{ t('audit.failed') }}</option>
            <option value="pending">{{ t('audit.pending') }}</option>
          </select>

          <select v-model="selectedResourceType" class="filter-select">
            <option value="">{{ t('audit.allResourceTypes') }}</option>
            <option value="vm">{{ t('vm.name') }}</option>
            <option value="disk">{{ t('disk.name') }}</option>
          </select>

          <select v-model="selectedProject" class="filter-select">
            <option value="">{{ t('audit.allProjects') }}</option>
            <option v-for="project in projects" :key="project" :value="project">
              {{ project }}
            </option>
          </select>
        </div>

        <div class="date-filter">
          <label>{{ t('audit.timeRange') }}:</label>
          <select v-model="dateRange" class="filter-select" @change="updateDateRange">
            <option value="7">{{ t('audit.last7Days') }}</option>
            <option value="30">{{ t('audit.last30Days') }}</option>
            <option value="90">{{ t('audit.last90Days') }}</option>
            <option value="custom">{{ t('audit.custom') }}</option>
          </select>

          <div v-if="dateRange === 'custom'" class="custom-date">
            <input
              v-model="startDate"
              type="date"
              class="date-input"
              @change="updateDateRange"
            />
            <span>{{ t('audit.to') }}</span>
            <input
              v-model="endDate"
              type="date"
              class="date-input"
              @change="updateDateRange"
            />
          </div>
        </div>
      </div>

      <div class="action-buttons">
        <button @click="refreshLogs" class="btn btn-primary" :disabled="loading">
          <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
          {{ t('common.refresh') }}
        </button>
        <button @click="exportLogs" class="btn btn-secondary" :disabled="loading">
          <i class="fas fa-download"></i>
          {{ t('audit.export') }}
        </button>
      </div>
    </div>

    <!-- 统计信息 -->
    <div class="stats-cards" v-if="stats">
      <div class="stat-card">
        <i class="fas fa-list"></i>
        <div class="stat-num">{{ stats.totalLogs }}</div>
        <div class="stat-label">{{ t('audit.totalRecords') }}</div>
      </div>
      <div class="stat-card success">
        <i class="fas fa-check-circle"></i>
        <div class="stat-num">{{ stats.successCount }}</div>
        <div class="stat-label">{{ t('audit.successfulOperations') }}</div>
      </div>
      <div class="stat-card error">
        <i class="fas fa-times-circle"></i>
        <div class="stat-num">{{ stats.failedCount }}</div>
        <div class="stat-label">{{ t('audit.failedOperations') }}</div>
      </div>
      <div class="stat-card">
        <i class="fas fa-users"></i>
        <div class="stat-num">{{ stats.uniqueUsers }}</div>
        <div class="stat-label">{{ t('audit.activeUsers') }}</div>
      </div>
    </div>

    <!-- 日志表格 -->
    <div class="logs-table-container">
      <table class="logs-table">
        <thead>
          <tr>
            <th @click="sortBy('timestamp')" class="sortable">
              {{ t('audit.time') }}
              <i class="fas fa-sort" :class="getSortIcon('timestamp')"></i>
            </th>
            <th @click="sortBy('user')" class="sortable">
              {{ t('audit.operator') }}
              <i class="fas fa-sort" :class="getSortIcon('user')"></i>
            </th>
            <th @click="sortBy('action')" class="sortable">
              {{ t('audit.actionType') }}
              <i class="fas fa-sort" :class="getSortIcon('action')"></i>
            </th>
            <th @click="sortBy('resourceName')" class="sortable">
              {{ t('audit.resourceName') }}
              <i class="fas fa-sort" :class="getSortIcon('resourceName')"></i>
            </th>
            <th @click="sortBy('resourceType')" class="sortable">
              {{ t('audit.resourceType') }}
              <i class="fas fa-sort" :class="getSortIcon('resourceType')"></i>
            </th>
            <th @click="sortBy('projectId')" class="sortable">
              {{ t('audit.projectId') }}
              <i class="fas fa-sort" :class="getSortIcon('projectId')"></i>
            </th>
            <th @click="sortBy('status')" class="sortable">
              {{ t('audit.status') }}
              <i class="fas fa-sort" :class="getSortIcon('status')"></i>
            </th>
            <th @click="sortBy('duration')" class="sortable">
              {{ t('audit.duration') }}
              <i class="fas fa-sort" :class="getSortIcon('duration')"></i>
            </th>
            <th>{{ t('audit.details') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="log in paginatedLogs" :key="log._id" class="log-row">
            <td class="timestamp">
              {{ formatDateTime(log.timestamp) }}
            </td>
            <td class="user">
              <span class="user-email">{{ log.user }}</span>
            </td>
            <td class="action">
              <span class="action-badge" :class="getActionClass(log.action)">
                {{ getActionDisplayName(log.action) }}
              </span>
            </td>
            <td class="resource-name">
              <span v-if="log.resourceName">{{ log.resourceName }}</span>
              <span v-else class="no-resource">-</span>
            </td>
            <td class="resource-type">
              <span v-if="log.resourceType" class="resource-badge" :class="log.resourceType">
                {{ log.resourceType.toUpperCase() }}
              </span>
              <span v-else class="no-resource">-</span>
            </td>
            <td class="project-id">
              <span class="project-badge">{{ log.projectId }}</span>
            </td>
            <td class="status">
              <span class="status-badge" :class="log.status">
                {{ getStatusDisplayName(log.status) }}
              </span>
            </td>
            <td class="duration">
              <span v-if="log.duration">{{ formatDuration(log.duration) }}</span>
              <span v-else>-</span>
            </td>
            <td class="details">
              <button @click="showLogDetails(log)" class="btn-details">
                <i class="fas fa-eye"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- 空状态 -->
      <div v-if="!loading && logs.length === 0" class="empty-state">
        <i class="fas fa-search"></i>
        <p>{{ t('audit.noMatchingLogs') }}</p>
        <button @click="clearFilters" class="btn btn-primary">{{ t('audit.clearFilters') }}</button>
      </div>

      <!-- 加载状态 -->
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>{{ t('common.loading') }}</p>
      </div>
    </div>

    <!-- 分页 -->
    <div class="pagination" v-if="totalPages > 1">
      <button
        @click="changePage(currentPage - 1)"
        :disabled="currentPage <= 1"
        class="btn-page"
      >
        <i class="fas fa-chevron-left"></i>
      </button>

      <span class="page-info">
        {{ t('audit.pageInfo', { current: currentPage, total: totalPages, records: totalLogs }) }}
      </span>

      <button
        @click="changePage(currentPage + 1)"
        :disabled="currentPage >= totalPages"
        class="btn-page"
      >
        <i class="fas fa-chevron-right"></i>
      </button>
    </div>

    <!-- 日志详情模态框 -->
    <div v-if="showDetailsModal" class="modal-overlay" @click="closeDetailsModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>{{ t('audit.logDetails') }}</h3>
          <button @click="closeDetailsModal" class="btn-close">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body" v-if="selectedLog">
          <div class="detail-section">
            <h4>{{ t('audit.basicInfo') }}</h4>
            <div class="detail-grid">
              <div class="detail-item">
                <label>{{ t('audit.operationTime') }}:</label>
                <span>{{ formatDateTime(selectedLog.timestamp) }}</span>
              </div>
              <div class="detail-item">
                <label>{{ t('audit.operator') }}:</label>
                <span>{{ selectedLog.user }}</span>
              </div>
              <div class="detail-item">
                <label>{{ t('audit.actionType') }}:</label>
                <span>{{ getActionDisplayName(selectedLog.action) }}</span>
              </div>
              <div class="detail-item">
                <label>{{ t('audit.status') }}:</label>
                <span class="status-badge" :class="selectedLog.status">
                  {{ getStatusDisplayName(selectedLog.status) }}
                </span>
              </div>
              <div class="detail-item">
                <label>{{ t('audit.resourceName') }}:</label>
                <span>{{ selectedLog.resourceName || '-' }}</span>
              </div>
              <div class="detail-item">
                <label>{{ t('audit.resourceType') }}:</label>
                <span>{{ selectedLog.resourceType || '-' }}</span>
              </div>
              <div class="detail-item">
                <label>{{ t('audit.projectId') }}:</label>
                <span>{{ selectedLog.projectId }}</span>
              </div>
              <div class="detail-item">
                <label>{{ t('audit.zone') }}:</label>
                <span>{{ selectedLog.zone || '-' }}</span>
              </div>
              <div class="detail-item">
                <label>{{ t('audit.duration') }}:</label>
                <span>{{ formatDuration(selectedLog.duration) }}</span>
              </div>
              <div class="detail-item">
                <label>{{ t('audit.ipAddress') }}:</label>
                <span>{{ selectedLog.ipAddress || '-' }}</span>
              </div>
            </div>
          </div>

          <div class="detail-section" v-if="selectedLog.details">
            <h4>{{ t('audit.detailedInfo') }}</h4>
            <pre class="details-json">{{ JSON.stringify(selectedLog.details, null, 2) }}</pre>
          </div>

          <div class="detail-section" v-if="selectedLog.error">
            <h4>{{ t('audit.errorInfo') }}</h4>
            <div class="error-message">{{ selectedLog.error }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useNotifications } from '../composables/useNotifications.js';
import { useResources } from '../composables/useResources.js';

const { t } = useI18n();

const { showNotification } = useNotifications();
const { currentProjectId, projects } = useResources();

// 响应式数据
const logs = ref([]);
const stats = ref(null);
const loading = ref(false);
const searchQuery = ref('');
const selectedAction = ref('');
const selectedStatus = ref('');
const selectedResourceType = ref('');
const selectedProject = ref(''); // 新增项目过滤器
const dateRange = ref('7');
const startDate = ref('');
const endDate = ref('');
const currentPage = ref(1);
const pageSize = ref(20);
const sortField = ref('timestamp');
const sortOrder = ref('desc');
const showDetailsModal = ref(false);
const selectedLog = ref(null);

// 可用的操作类型
const availableActions = ref([
  'vm_create', 'vm_start', 'vm_stop', 'vm_delete',
  'disk_create', 'disk_delete', 'disk_attach', 'disk_detach'
]);

// 计算属性
const filteredLogs = computed(() => {
  let filtered = [...logs.value];

  // 搜索过滤
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    filtered = filtered.filter(log =>
      log.user.toLowerCase().includes(query) ||
      (log.resourceName && log.resourceName.toLowerCase().includes(query)) ||
      log.projectId.toLowerCase().includes(query)
    );
  }

  // 操作类型过滤
  if (selectedAction.value) {
    filtered = filtered.filter(log => log.action === selectedAction.value);
  }

  // 状态过滤
  if (selectedStatus.value) {
    filtered = filtered.filter(log => log.status === selectedStatus.value);
  }

  // 资源类型过滤
  if (selectedResourceType.value) {
    filtered = filtered.filter(log => log.resourceType === selectedResourceType.value);
  }

  // 项目过滤
  if (selectedProject.value) {
    filtered = filtered.filter(log => log.projectId === selectedProject.value);
  }

  // 排序
  filtered.sort((a, b) => {
    let aVal = a[sortField.value];
    let bVal = b[sortField.value];

    if (sortField.value === 'timestamp') {
      aVal = new Date(aVal);
      bVal = new Date(bVal);
    }

    if (sortOrder.value === 'asc') {
      return aVal > bVal ? 1 : -1;
    } else {
      return aVal < bVal ? 1 : -1;
    }
  });

  return filtered;
});

const totalLogs = computed(() => filteredLogs.value.length);
const totalPages = computed(() => Math.ceil(totalLogs.value / pageSize.value));
const paginatedLogs = computed(() => {
  const start = (currentPage.value - 1) * pageSize.value;
  const end = start + pageSize.value;
  return filteredLogs.value.slice(start, end);
});

// 方法
const fetchLogs = async () => {
  loading.value = true;
  try {
    const params = new URLSearchParams();

    // 添加项目过滤
    if (selectedProject.value) {
      params.append('projectId', selectedProject.value);
    }

    // 添加日期范围
    if (dateRange.value === 'custom') {
      if (startDate.value) {params.append('startDate', startDate.value);}
      if (endDate.value) {params.append('endDate', endDate.value);}
    } else {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - parseInt(dateRange.value, 10));
      params.append('startDate', startDate.toISOString());
      params.append('endDate', endDate.toISOString());
    }

    params.append('limit', '1000'); // 获取足够的数据用于前端过滤

    const response = await fetch(`/api/audit/logs?${params}`);
    const data = await response.json();

    if (data.success) {
      logs.value = data.data;
      await fetchStats();
    } else {
      throw new Error(data.error || t('audit.fetchLogsFailed'));
    }
  } catch (error) {
    console.error(t('audit.fetchLogsFailed'), error);
    showNotification(`${t('audit.fetchLogsFailed')}: ${error.message}`, 'error');
  } finally {
    loading.value = false;
  }
};

const fetchStats = async () => {
  try {
    const params = new URLSearchParams();

    // 添加项目过滤
    if (selectedProject.value) {
      params.append('projectId', selectedProject.value);
    }

    if (dateRange.value === 'custom') {
      if (startDate.value) {params.append('startDate', startDate.value);}
      if (endDate.value) {params.append('endDate', endDate.value);}
    } else {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - parseInt(dateRange.value, 10));
      params.append('startDate', startDate.toISOString());
      params.append('endDate', endDate.toISOString());
    }

    const response = await fetch(`/api/audit/stats?${params}`);
    const data = await response.json();

    if (data.success) {
      stats.value = {
        totalLogs: logs.value.length,
        successCount: logs.value.filter(log => log.status === 'success').length,
        failedCount: logs.value.filter(log => log.status === 'failed').length,
        uniqueUsers: new Set(logs.value.map(log => log.user)).size
      };
    }
  } catch (error) {
    console.error(t('audit.fetchStatsFailed'), error);
  }
};

const updateDateRange = () => {
  if (dateRange.value === 'custom') {
    if (!startDate.value) {
      const date = new Date();
      date.setDate(date.getDate() - 7);
      startDate.value = date.toISOString().split('T')[0];
    }
    if (!endDate.value) {
      endDate.value = new Date().toISOString().split('T')[0];
    }
  }
  currentPage.value = 1;
  fetchLogs();
};

const debounceSearch = () => {
  clearTimeout(searchTimeout);
  searchTimeout = setTimeout(() => {
    currentPage.value = 1;
  }, 300);
};

const sortBy = (field) => {
  if (sortField.value === field) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortField.value = field;
    sortOrder.value = 'asc';
  }
  currentPage.value = 1;
};

const changePage = (page) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page;
  }
};

const showLogDetails = (log) => {
  selectedLog.value = log;
  showDetailsModal.value = true;
};

const closeDetailsModal = () => {
  showDetailsModal.value = false;
  selectedLog.value = null;
};

const refreshLogs = () => {
  fetchLogs();
};

const clearFilters = () => {
  searchQuery.value = '';
  selectedAction.value = '';
  selectedStatus.value = '';
  selectedResourceType.value = '';
  selectedProject.value = currentProjectId.value || ''; // 重置为当前项目
  dateRange.value = '7';
  startDate.value = '';
  endDate.value = '';
  currentPage.value = 1;
  fetchLogs();
};

const exportLogs = () => {
  const csvContent = generateCSV(filteredLogs.value);
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `audit_logs_${new Date().toISOString().split('T')[0]}.csv`;
  link.click();
};

const generateCSV = (logs) => {
  const headers = [t('audit.time'), t('audit.operator'), t('audit.actionType'), t('audit.resourceName'), t('audit.resourceType'), t('audit.projectId'), t('audit.status'), t('audit.duration'), t('audit.ipAddress')];
  const rows = logs.map(log => [
    formatDateTime(log.timestamp),
    log.user,
    getActionDisplayName(log.action),
    log.resourceName || '',
    log.resourceType || '',
    log.projectId,
    getStatusDisplayName(log.status),
    formatDuration(log.duration),
    log.ipAddress || ''
  ]);

  return [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
};

// 工具函数
const getActionDisplayName = (action) => {
  const actionNames = {
    'vm_create': t('audit.actions.vmCreate'),
    'vm_start': t('audit.actions.vmStart'),
    'vm_stop': t('audit.actions.vmStop'),
    'vm_delete': t('audit.actions.vmDelete'),
    'disk_create': t('audit.actions.diskCreate'),
    'disk_delete': t('audit.actions.diskDelete'),
    'disk_attach': t('audit.actions.diskAttach'),
    'disk_detach': t('audit.actions.diskDetach')
  };
  return actionNames[action] || action;
};

const getStatusDisplayName = (status) => {
  const statusNames = {
    'success': t('audit.statuses.success'),
    'failed': t('audit.statuses.failed'),
    'pending': t('audit.statuses.pending')
  };
  return statusNames[status] || status;
};

const getActionClass = (action) => {
  if (action.includes('create')) {return 'create';}
  if (action.includes('delete')) {return 'delete';}
  if (action.includes('start')) {return 'start';}
  if (action.includes('stop')) {return 'stop';}
  if (action.includes('attach')) {return 'attach';}
  if (action.includes('detach')) {return 'detach';}
  return 'default';
};

const getSortIcon = (field) => {
  if (sortField.value !== field) {return '';}
  return sortOrder.value === 'asc' ? 'fa-sort-up' : 'fa-sort-down';
};

const formatDateTime = (timestamp) => {
  return new Date(timestamp).toLocaleString('zh-CN');
};

const formatDuration = (duration) => {
  if (!duration) {return '-';}
  if (duration < 1000) {return `${duration}ms`;}
  return `${(duration / 1000).toFixed(2)}s`;
};

// 生命周期
onMounted(() => {
  // 设置默认项目为当前项目
  selectedProject.value = currentProjectId.value || '';
  updateDateRange();
  fetchLogs();
});

// 监听器
watch([selectedAction, selectedStatus, selectedResourceType, selectedProject], () => {
  currentPage.value = 1;
});

// 监听当前项目变化
watch(currentProjectId, (newProjectId) => {
  if (newProjectId && !selectedProject.value) {
    selectedProject.value = newProjectId;
  }
});

let searchTimeout = null;
</script>

<style scoped>
.audit-log-view {
  padding: 20px;
  background: #f8f9fa;
  min-height: 100vh;
}

.audit-header {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 20px;
}

.search-filters {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.search-group {
  position: relative;
}

.search-input {
  width: 100%;
  padding: 10px 40px 10px 15px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
}

.search-icon {
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: #666;
}

.filter-group {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.filter-select {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  min-width: 120px;
}

.date-filter {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.custom-date {
  display: flex;
  align-items: center;
  gap: 8px;
}

.date-input {
  padding: 6px 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.action-buttons {
  display: flex;
  gap: 10px;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
}

.btn-primary {
  background: #007bff;
  color: white;
}

.btn-primary:hover {
  background: #0056b3;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background: #545b62;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.stats-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  margin-bottom: 20px;
}

.stat-card {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  text-align: center;
  border-left: 4px solid #007bff;
}

.stat-card.success {
  border-left-color: #28a745;
}

.stat-card.error {
  border-left-color: #dc3545;
}

.stat-card i {
  font-size: 24px;
  color: #666;
  margin-bottom: 10px;
}

.stat-num {
  font-size: 28px;
  font-weight: bold;
  color: #333;
  margin-bottom: 5px;
}

.stat-label {
  font-size: 14px;
  color: #666;
}

.logs-table-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  overflow: hidden;
  margin-bottom: 20px;
}

.logs-table {
  width: 100%;
  border-collapse: collapse;
}

.logs-table th {
  background: #f8f9fa;
  padding: 12px;
  text-align: left;
  font-weight: 600;
  color: #333;
  border-bottom: 1px solid #dee2e6;
  cursor: pointer;
  user-select: none;
}

.logs-table th.sortable:hover {
  background: #e9ecef;
}

.logs-table th i {
  margin-left: 5px;
  color: #666;
}

.logs-table td {
  padding: 12px;
  border-bottom: 1px solid #f1f3f4;
  vertical-align: middle;
}

.log-row:hover {
  background: #f8f9fa;
}

.timestamp {
  font-family: monospace;
  font-size: 13px;
  color: #666;
}

.user-email {
  font-weight: 500;
  color: #333;
}

.action-badge {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
}

.action-badge.create {
  background: #d4edda;
  color: #155724;
}

.action-badge.delete {
  background: #f8d7da;
  color: #721c24;
}

.action-badge.start {
  background: #d1ecf1;
  color: #0c5460;
}

.action-badge.stop {
  background: #fff3cd;
  color: #856404;
}

.action-badge.attach {
  background: #d1ecf1;
  color: #0c5460;
}

.action-badge.detach {
  background: #f8d7da;
  color: #721c24;
}

.resource-badge {
  padding: 2px 6px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
}

.resource-badge.vm {
  background: #e2e3e5;
  color: #383d41;
}

.resource-badge.disk {
  background: #d1ecf1;
  color: #0c5460;
}

.project-badge {
  font-family: monospace;
  font-size: 12px;
  color: #666;
  background: #f8f9fa;
  padding: 2px 6px;
  border-radius: 4px;
}

.status-badge {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.status-badge.success {
  background: #d4edda;
  color: #155724;
}

.status-badge.failed {
  background: #f8d7da;
  color: #721c24;
}

.status-badge.pending {
  background: #fff3cd;
  color: #856404;
}

.duration {
  font-family: monospace;
  font-size: 13px;
  color: #666;
}

.btn-details {
  background: none;
  border: none;
  color: #007bff;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: background 0.2s;
}

.btn-details:hover {
  background: #e3f2fd;
}

.empty-state, .loading-state {
  text-align: center;
  padding: 60px 20px;
  color: #666;
}

.empty-state i, .loading-state i {
  font-size: 48px;
  margin-bottom: 20px;
  color: #ddd;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #007bff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 15px;
  margin-top: 20px;
}

.btn-page {
  background: white;
  border: 1px solid #ddd;
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-page:hover:not(:disabled) {
  background: #f8f9fa;
}

.btn-page:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  font-size: 14px;
  color: #666;
}

/* 模态框样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 8px;
  max-width: 800px;
  width: 90%;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal-header {
  padding: 20px;
  border-bottom: 1px solid #dee2e6;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h3 {
  margin: 0;
  color: #333;
}

.btn-close {
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: #666;
  padding: 4px;
  border-radius: 4px;
  transition: background 0.2s;
}

.btn-close:hover {
  background: #f8f9fa;
}

.modal-body {
  padding: 20px;
  overflow-y: auto;
  flex: 1;
}

.detail-section {
  margin-bottom: 30px;
}

.detail-section h4 {
  margin: 0 0 15px 0;
  color: #333;
  font-size: 16px;
  border-bottom: 2px solid #007bff;
  padding-bottom: 5px;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 15px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.detail-item label {
  font-weight: 600;
  color: #666;
  font-size: 12px;
  text-transform: uppercase;
}

.detail-item span {
  color: #333;
  font-size: 14px;
}

.details-json {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 6px;
  font-family: monospace;
  font-size: 12px;
  line-height: 1.4;
  overflow-x: auto;
  white-space: pre-wrap;
}

.error-message {
  background: #f8d7da;
  color: #721c24;
  padding: 15px;
  border-radius: 6px;
  border: 1px solid #f5c6cb;
  font-family: monospace;
  font-size: 13px;
  line-height: 1.4;
}

.no-resource {
  color: #999;
  font-style: italic;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .audit-header {
    flex-direction: column;
    align-items: stretch;
  }

  .action-buttons {
    justify-content: center;
  }

  .filter-group {
    flex-direction: column;
  }

  .date-filter {
    flex-direction: column;
    align-items: stretch;
  }

  .logs-table {
    font-size: 12px;
  }

  .logs-table th,
  .logs-table td {
    padding: 8px 6px;
  }

  .detail-grid {
    grid-template-columns: 1fr;
  }
}
</style>
